// Guess is a weaker form of Dirty and indicates the system has modified something by guessing.
// Dirty indicates the user has made a change.
export enum DirtyState {
  Clean,
  Guess,
  Dirty,
}

export class InteractionInfo {
  dirty: DirtyState
  touched: boolean

  constructor(dirty: DirtyState, touched: boolean) {
    this.dirty = dirty
    this.touched = touched
  }

  isDirty() {
    return this.dirty === DirtyState.Dirty || this.dirty === DirtyState.Guess
  }

  getDirty() {
    return this.dirty
  }

  markDirty(dirty: DirtyState = DirtyState.Dirty) {
    this.dirty = dirty
  }

  isTouched() {
    return this.touched
  }

  markTouched() {
    this.touched = true
  }
}

export class InteractionMap<T> {
  items: Map<T, InteractionInfo>

  constructor() {
    this.items = new Map<T, InteractionInfo>()
  }

  get(key: T): InteractionInfo | undefined {
    return this.items.get(key)
  }

  isDirty(key: T): boolean {
    const info = this.items.get(key)

    if (info === undefined) {
      return false
    }

    return info.isDirty()
  }

  getDirty(key: T): DirtyState {
    const info = this.items.get(key)

    if (info === undefined) {
      return DirtyState.Clean
    }

    return info.getDirty()
  }

  markDirty(key: T, dirty: DirtyState = DirtyState.Dirty) {
    if (!this.items.has(key)) {
      const info = new InteractionInfo(dirty, false)
      this.items.set(key, info)
    } else {
      const info = this.items.get(key)

      if (info!.getDirty() !== dirty) {
        info!.markDirty(dirty)
      }
    }
  }

  markAllTouched(keys: T[]) {
    keys.forEach((key) => this.markTouched(key))
  }

  isTouched(key: T): boolean {
    const info = this.items.get(key)

    if (info === undefined) {
      return false
    }

    return info.isTouched()
  }

  markTouched(key: T) {
    if (!this.items.has(key)) {
      const info = new InteractionInfo(DirtyState.Clean, true)
      this.items.set(key, info)
    } else {
      const info = this.items.get(key)

      if (!info!.touched) {
        info!.touched = true
      }
    }
  }
}
